import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SmilePlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REACTIONS = ['🙏', '❤️', '👍', '👏', '🎉', '😂', '😢', '🔥'];

const MessageReactions = ({ communityId, messageId, reactions = {}, currentUserId, onReactionChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleReaction = async (emoji) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/communities/${communityId}/messages/${messageId}/reactions`,
        { emoji },
        { withCredentials: true }
      );
      
      if (onReactionChange) {
        onReactionChange(response.data.reactions);
      }
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to add reaction');
    } finally {
      setLoading(false);
    }
  };

  // Convert reactions object to display array
  const reactionDisplay = Object.entries(reactions).map(([emoji, users]) => ({
    emoji,
    count: users.length,
    hasReacted: users.includes(currentUserId),
    users
  }));

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Existing reactions */}
      <TooltipProvider>
        {reactionDisplay.map(({ emoji, count, hasReacted, users }) => (
          <Tooltip key={emoji}>
            <TooltipTrigger asChild>
              <button
                onClick={() => handleReaction(emoji)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                  hasReacted 
                    ? 'bg-primary/20 border border-primary/30' 
                    : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                disabled={loading}
              >
                <span>{emoji}</span>
                <span className="font-medium">{count}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {users.length > 0 
                  ? `${users.length} ${users.length === 1 ? 'person' : 'people'} reacted`
                  : 'No reactions'}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
      </TooltipProvider>

      {/* Add reaction button */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid={`add-reaction-${messageId}`}
          >
            <SmilePlus className="w-4 h-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" side="top">
          <div className="flex gap-1">
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleReaction(emoji)}
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors text-lg"
                disabled={loading}
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MessageReactions;
